import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Ghost } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { listAudit } from "@/services/api/audit";

interface AuditEntry {
  action: string;
  occurred_at: string;
  actor_email?: string | null;
  actor_user_id: number | null;
  changes: { field_name: string; old_value: string | null; new_value: string | null }[];
}

/** Internal bookkeeping columns that carry no user-facing meaning. */
const HIDDEN_FIELDS = new Set(["deleted_at", "row_version"]);

/**
 * Replay the entity's audit history (oldest first) up to and including the
 * newest `delete` entry, rebuilding the record's last known state. Insert and
 * update entries contribute their new values; a hard-delete entry contributes
 * its old values (the full pre-delete snapshot).
 */
function replaySnapshot(entries: AuditEntry[]): { fields: [string, string][]; deletedAt: string } {
  const ordered = [...entries].sort(
    (a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
  );
  const lastDelete = ordered.map((e) => e.action).lastIndexOf("delete");
  const state = new Map<string, string>();
  for (const entry of lastDelete >= 0 ? ordered.slice(0, lastDelete + 1) : ordered) {
    for (const change of entry.changes) {
      const value =
        entry.action === "delete" ? (change.old_value ?? "") : (change.new_value ?? "");
      state.set(change.field_name, value);
    }
  }
  const anchor = lastDelete >= 0 ? ordered[lastDelete] : ordered[ordered.length - 1];
  const fields = [...state.entries()]
    .filter(([name]) => !HIDDEN_FIELDS.has(name))
    .map(([name, value]) => [name, value] as [string, string]);
  return { fields, deletedAt: anchor?.occurred_at ?? "" };
}

/**
 * Compact "deleted record" banner for a ghost-styled record view: the real,
 * retained record is displayed in place (e.g. inside a view dialog) framed
 * with the dashed yellow ghost border.
 */
export function AuditGhostBanner() {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-2">
      <Ghost
        className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-audit-highlight)]"
        aria-hidden
      />
      <div>
        <div className="text-sm font-semibold">{t("admin.auditDeletedRecord")}</div>
        <p className="text-xs text-muted-foreground">{t("admin.auditDeletedRecordHint")}</p>
      </div>
    </div>
  );
}

/**
 * Ghost copy of a record rebuilt from the audit trail.
 *
 * Two variants:
 * - `deleted` (default): shown on record pages when an admin follows an audit
 *   deep-link for a record that no longer appears in the list — the copy is
 *   framed with a thick dashed yellow border so it is obvious which record
 *   was deleted.
 * - `snapshot`: shown in the audit trail's fallback dialog for entities that
 *   have no record page — the same replay, framed as a read-only snapshot.
 */
export function AuditGhostRecord({
  entityType,
  entityId,
  variant = "deleted",
}: {
  entityType: string;
  entityId: string | null;
  variant?: "deleted" | "snapshot";
}) {
  const { t } = useTranslation();
  const history = useQuery({
    queryKey: ["audit-ghost", entityType, entityId],
    queryFn: () =>
      listAudit({ entity_type: entityType, entity_id: entityId ?? undefined, limit: 500 }),
  });

  const snapshot = useMemo(
    () => (history.data ? replaySnapshot(history.data.items as AuditEntry[]) : null),
    [history.data],
  );

  return (
    <div className="audit-ghost-record p-4" data-audit-ghost="true">
      <div className="flex items-center gap-2">
        <Ghost className="h-5 w-5 shrink-0 text-[var(--color-audit-highlight)]" aria-hidden />
        <div className="text-sm font-semibold">
          {variant === "snapshot" ? t("admin.auditSnapshot") : t("admin.auditDeletedRecord")}
        </div>
        <div className="font-mono text-xs text-muted-foreground">
          {entityType}
          {entityId ? ` #${entityId}` : ""}
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {variant === "snapshot"
          ? t("admin.auditSnapshotHint")
          : t("admin.auditDeletedRecordHint")}
      </p>

      {history.isLoading ? (
        <Skeleton className="mt-3 h-16 w-full" />
      ) : snapshot && snapshot.fields.length > 0 ? (
        <>
          <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
            {snapshot.fields.map(([name, value]) => (
              <div key={name} className="flex items-baseline justify-between gap-2 text-xs">
                <span className="font-mono text-muted-foreground">{name}</span>
                <span className="truncate font-mono">{value || "—"}</span>
              </div>
            ))}
          </div>
          {snapshot.deletedAt ? (
            <div className="mt-3 text-xs text-muted-foreground">
              {t("admin.changedAt")}: {new Date(snapshot.deletedAt).toLocaleString()}
            </div>
          ) : null}
        </>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">{t("admin.auditNoSnapshot")}</p>
      )}
    </div>
  );
}